import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Box, Button, Card, Divider, Grid, IconButton, TextField, Typography, Alert } from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";

import { AutoComplete, ControlledAutocomplete, FieldControl } from "../../../shared/components";
import { AutocompleteOption, ControlledAutocompleteVariant } from "../../../shared/components/ControlledAutocomplete";
import LocationSelection from "../../../shared/components/LocationSelection";
import { AssetHeaderProps } from "../types";
import { useAddProject } from "../index";
import { useDisplayField } from "../../../hooks/useDisplayField";

const AssetHeader: React.FC<AssetHeaderProps> = ({ title, onDelete }) => (
    <Box display="flex" justifyContent="space-between" alignItems="center" bgcolor="action.hover" py={1} px={2}>
        <Typography variant="subtitle1" fontWeight={600}>
            {title}
        </Typography>
        <IconButton color="error" onClick={onDelete} size="small">
            <DeleteIcon />
        </IconButton>
    </Box>
);

const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 5; i <= currentYear + 5; i++) {
        years.push({ value: `FY${i.toString().slice(-2)}`, label: i });
    }
    return years;
};

export const AddProject: React.FC<{ isEdit: boolean }> = ({ isEdit }) => {

    const { projectId } = useParams();

    const navigate = useNavigate();

    const {
        formData, fieldErrors, stakeholders, assets, assetErrors, openStakeholderDialog, isSubmitting, submitMessage,
        handleAddAsset, handleRemoveAsset, handleSubmit, handleFieldChange, handleAssetChange, setSubmitMessage,
        handleAddStakeholder, handleRemoveStakeholder, setOpenStakeholderDialog, setFormData
    } = useAddProject({ isEdit, projectId });

    const { getFilterOptions } = useDisplayField([]);

    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const handleOpenDialog = () => {
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
    };

    const handleLocationSelect = (location: any) => {
        setFormData({
            ...formData,
            location: location,
            what3_words: location.what3_words,
            lat: location.coordinates[1],
            long: location.coordinates[0],
        });
    };

    const [countdown, setCountdown] = useState(5);

    useEffect(() => {
        if (submitMessage) {
            setCountdown(5);

            const interval = setInterval(() => {
                setCountdown((prev) => prev - 1);
            }, 1000);

            const timeout = setTimeout(() => {
                navigate(-1);
            }, 5000);

            return () => {
                clearInterval(interval);
                clearTimeout(timeout);
            };
        }
    }, [submitMessage]);

    if (submitMessage) {
        return (
            <Box m={2} display="flex" justifyContent="center" alignItems="center" height="100vh">
                <Alert
                    severity={submitMessage.type}
                    onClose={() => setSubmitMessage(null)}
                    sx={{ mb: 3 }}
                >
                    {submitMessage.message} <br />
                    Redirecting in {countdown} seconds...
                </Alert>
            </Box>
        );
    }

    return (
        <Box p={2}>

            <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
                    <Typography variant="body1" fontWeight={500} color="text.primary">Project Information</Typography>
                    <Typography variant="body2" color="text.secondary">Define the core project details and identifiers.</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 8, lg: 8 }}>
                    <Grid container spacing={2}>

                        <Grid size={{ xs: 12 }}>
                            <FieldControl
                                variant="input"
                                keyName="title"
                                label='Title'
                                value={formData.title}
                                onChange={handleFieldChange}
                                error={!!fieldErrors.title}
                                helperText={fieldErrors.title}
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <FieldControl
                                variant="outlined"
                                keyName="description"
                                label='Description'
                                value={formData.description}
                                onChange={handleFieldChange}
                                error={!!fieldErrors.description}
                                helperText={fieldErrors.description}
                                multiline
                                rows={3}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <ControlledAutocomplete
                                type={"PORTFOLIO"}
                                value={formData.portfolio}
                                label="Project Portfolio"
                                onSelect={(value: AutocompleteOption | AutocompleteOption[] | null, type: ControlledAutocompleteVariant) => {
                                    handleFieldChange("portfolio", value);
                                }}
                                error={!!fieldErrors.portfolio}
                                helperText={fieldErrors.portfolio}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <ControlledAutocomplete
                                type={"PROGRAMME"}
                                value={formData.programme}
                                label="Programme"
                                onSelect={(value: AutocompleteOption | AutocompleteOption[] | null, type: ControlledAutocompleteVariant) => {
                                    handleFieldChange("programme", value);
                                }}
                                error={!!fieldErrors.programme}
                                helperText={fieldErrors.programme}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <AutoComplete
                                value={formData.workstream}
                                onSelect={(value) => handleFieldChange('workstream', value)}
                                name="workstream"
                                label="Workstream"
                                error={!!fieldErrors.workstream}
                                helperText={fieldErrors.workstream}
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <FieldControl
                                keyName="project_scope"
                                label='Project Scope'
                                variant="outlined"
                                multiline
                                rows={3}
                                value={formData.project_scope}
                                onChange={handleFieldChange}
                                error={!!fieldErrors.project_scope}
                                helperText={fieldErrors.project_scope}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <FieldControl
                                keyName="wbs_code_id"
                                label='WBS Code'
                                variant="input"
                                value={formData.wbs_code_id}
                                onChange={handleFieldChange}
                                error={!!fieldErrors.wbs_code_id}
                                helperText={fieldErrors.wbs_code_id}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <FieldControl
                                keyName="project_budget"
                                label='Project Budget'
                                variant="input"
                                value={formData.project_budget}
                                onChange={handleFieldChange}
                                error={!!fieldErrors.project_budget}
                                helperText={fieldErrors.project_budget}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <FieldControl
                                keyName="project_year"
                                label="Reporting Period"
                                variant="select"
                                options={getYearOptions()}
                                value={formData.project_year}
                                onChange={handleFieldChange}
                                error={!!fieldErrors.project_year}
                                helperText={fieldErrors.project_year}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <AutoComplete
                                value={formData.work_type_id}
                                onSelect={(value) => handleFieldChange("work_type_id", value)}
                                name="worktype"
                                label="Work Type"
                                error={!!fieldErrors.work_type_id}
                                helperText={fieldErrors.work_type_id}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <AutoComplete
                                value={formData.work_group_id}
                                onSelect={(value) => handleFieldChange("work_group_id", value)}
                                name="worktype-group"
                                label="Work Type ID"
                                error={!!fieldErrors.work_group_id}
                                helperText={fieldErrors.work_group_id}
                            />
                        </Grid>

                    </Grid>

                </Grid>

            </Grid>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={3}>

                <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
                    <Typography variant="body1" color="text.primary">Location & Site Details</Typography>
                    <Typography variant="body2" color="text.secondary">Specify geographic and operational site information.</Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 8, lg: 8 }}>

                    <Grid container spacing={2}>

                        {/* <Grid size={{ xs: 12 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={false}
                                        onChange={(e) => handleFieldChange("siteNotApplicable", e.target.checked)}
                                    />
                                }
                                label='Site Not Applicable'
                            />
                        </Grid> */}

                        <Grid size={{ xs: 12, md: 12 }}>
                            <AutoComplete
                                value={formData.site_name}
                                onSelect={(value) => handleFieldChange("site_name", value)}
                                name="site"
                                label="Site Name"
                                error={!!fieldErrors.site_name}
                                helperText={fieldErrors.site_name}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <AutoComplete
                                value={formData.region}
                                onSelect={(value) => handleFieldChange('region', value)}
                                name="region"
                                label="Network"
                                error={!!fieldErrors.region}
                                helperText={fieldErrors.region}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <AutoComplete
                                value={formData.network}
                                onSelect={(value) => handleFieldChange('network', value)}
                                name="network"
                                label="Organization"
                                error={!!fieldErrors.network}
                                helperText={fieldErrors.network}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <AutoComplete
                                value={formData.coamArea}
                                onSelect={(value) => handleFieldChange('coamArea', value)}
                                name="coam-area"
                                label="Coam Area"
                                error={!!fieldErrors.coamArea}
                                helperText={fieldErrors.coamArea}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                sx={{ width: '100%' }}
                                label={'Location'}
                                value={formData?.location?.address || ''}
                                onClick={handleOpenDialog}
                                error={!!fieldErrors.location}
                                helperText={fieldErrors.location}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                sx={{ width: '100%' }}
                                label={'What3 Words'}
                                disabled={true}
                                value={formData?.what3_words || ''}
                                error={!!fieldErrors.location}
                                helperText={fieldErrors.location}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <AutoComplete
                                value={formData.county}
                                onSelect={(value) => handleFieldChange("county", value)}
                                name="county"
                                label="County"
                                error={!!fieldErrors.county}
                                helperText={fieldErrors.county}
                            />
                        </Grid>

                    </Grid>

                </Grid>

            </Grid>

            <Divider sx={{ my: 2 }} />

            {
                !isEdit && (
                    <>

                        <Grid container spacing={3}>

                            <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
                                <Typography variant="body1" color="text.primary">Asset details</Typography>
                                <Typography variant="body2" color="text.secondary">Link assets or deliverables associated with this project.</Typography>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 12, md: 8, lg: 8 }}>
                                {assets.map((asset, index) => (
                                    <Card key={asset.id} sx={{ mb: 2 }}>

                                        <AssetHeader
                                            title={`Asset ${index + 1}`}
                                            onDelete={() => handleRemoveAsset(asset.id)}
                                        />

                                        <Grid container spacing={2} p={2}>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <ControlledAutocomplete
                                                    type={"PROGRAMME"}
                                                    value={asset.asset_programme}
                                                    label="Programme"
                                                    onSelect={(value: AutocompleteOption | AutocompleteOption[] | null, type: ControlledAutocompleteVariant) => {
                                                        handleAssetChange(asset.id, "asset_programme", value);
                                                    }}
                                                    error={!!(assetErrors[asset.id]?.asset_programme)}
                                                    helperText={assetErrors[asset.id]?.asset_programme}
                                                />
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <FieldControl
                                                    keyName="asset_category"
                                                    label='Asset Category'
                                                    variant="select"
                                                    value={asset.asset_category}
                                                    options={getFilterOptions("ASSET_CATEGORY").map((option) => ({ label: option.display_text, value: option.key }))}
                                                    onChange={(_key: string, value: any) => handleAssetChange(asset.id, "asset_category", value)}
                                                    error={!!(assetErrors[asset.id]?.asset_category)}
                                                    helperText={assetErrors[asset.id]?.asset_category}
                                                />
                                            </Grid>

                                            {
                                                (asset.asset_programme && asset.asset_programme.id == 6) &&
                                                (
                                                    <Grid size={{ xs: 12, md: 6 }}>
                                                        <FieldControl
                                                            keyName="narms_value"
                                                            label='NARMS Value'
                                                            variant="input"
                                                            value={asset.narms_value}
                                                            onChange={(_key: string, value: any) => handleAssetChange(asset.id, "narms_value", value)}
                                                            error={!!(assetErrors[asset.id]?.narms_value)}
                                                            helperText={assetErrors[asset.id]?.narms_value}
                                                        />
                                                    </Grid>
                                                )
                                            }

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <AutoComplete
                                                    value={asset.iln_number_id}
                                                    onSelect={(value) => handleAssetChange(asset.id, "iln_number_id", value)}
                                                    name="iln"
                                                    label="HCode"
                                                    error={!!(assetErrors[asset.id]?.iln_number_id)}
                                                    helperText={assetErrors[asset.id]?.iln_number_id}
                                                />
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <FieldControl
                                                    keyName="functional_location"
                                                    label='Functional Location'
                                                    variant="input"
                                                    value={asset.functional_location}
                                                    onChange={(_key: string, value: any) => handleAssetChange(asset.id, "functional_location", value)}
                                                    error={!!(assetErrors[asset.id]?.functional_location)}
                                                    helperText={assetErrors[asset.id]?.functional_location}
                                                />
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <FieldControl
                                                    keyName="asset_budget"
                                                    label='Asset Budget'
                                                    variant="input"
                                                    value={asset.asset_budget}
                                                    onChange={(_key: string, value: any) => handleAssetChange(asset.id, "asset_budget", value)}
                                                    error={!!(assetErrors[asset.id]?.asset_budget)}
                                                    helperText={assetErrors[asset.id]?.asset_budget}
                                                />
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <FieldControl
                                                    keyName="asset_type"
                                                    label='Asset Type'
                                                    variant="select"
                                                    value={asset.asset_type}
                                                    options={getFilterOptions("ASSET_TYPE").map((option) => ({ label: option.display_text, value: option.key }))}
                                                    onChange={(_key: string, value: any) => handleAssetChange(asset.id, "asset_type", value)}
                                                    error={!!(assetErrors[asset.id]?.asset_type)}
                                                    helperText={assetErrors[asset.id]?.asset_type}
                                                />
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <FieldControl
                                                    keyName="asset_description"
                                                    label='Asset Description'
                                                    variant="input"
                                                    value={asset.asset_description}
                                                    onChange={(_key: string, value: any) => handleAssetChange(asset.id, "asset_description", value)}
                                                    error={!!(assetErrors[asset.id]?.asset_description)}
                                                    helperText={assetErrors[asset.id]?.asset_description}
                                                />
                                            </Grid>

                                        </Grid>

                                    </Card>
                                ))}
                                <Button
                                    variant="text"
                                    startIcon={<AddIcon color="error" />}
                                    onClick={handleAddAsset}
                                    sx={{ color: "error.main", alignSelf: "start" }}
                                >
                                    ADD ASSET
                                </Button>
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        <Grid container spacing={3}>

                            <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
                                <Typography variant="body1" color="text.primary">Location & Site Details</Typography>
                                <Typography variant="body2" color="text.secondary">Specify geographic and operational site information.</Typography>
                            </Grid>

                            <Grid container spacing={2} sx={{ flex: 1 }}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <FieldControl
                                        keyName="planned_start_date"
                                        label='Planned Start Date'
                                        variant="date"
                                        value={formData.planned_start_date}
                                        onChange={handleFieldChange}
                                        error={!!fieldErrors.planned_start_date}
                                        helperText={fieldErrors.planned_start_date}
                                        maxDate={formData.planned_end_date}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <FieldControl
                                        keyName="planned_end_date"
                                        label='Planned End Date'
                                        variant="date"
                                        value={formData.planned_end_date}
                                        onChange={handleFieldChange}
                                        error={!!fieldErrors.planned_end_date}
                                        helperText={fieldErrors.planned_end_date}
                                        minDate={formData.planned_start_date}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <FieldControl
                                        keyName="planned_commissioning_date"
                                        label='Planned Commissioning Date'
                                        variant="date"
                                        value={formData.planned_commissioning_date}
                                        onChange={handleFieldChange}
                                        error={!!fieldErrors.planned_commissioning_date}
                                        helperText={fieldErrors.planned_commissioning_date}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <FieldControl
                                        keyName="planned_close_up_date"
                                        label='Planned Project End Date'
                                        variant="date"
                                        value={formData.planned_close_up_date}
                                        onChange={handleFieldChange}
                                        error={!!fieldErrors.planned_close_up_date}
                                        helperText={fieldErrors.planned_close_up_date}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12 }}>
                                    <AutoComplete
                                        value={formData.phase_template}
                                        onSelect={(value) => handleFieldChange('phase_template', value)}
                                        name="templates"
                                        label="Phase Template"
                                        error={!!fieldErrors.phase_template}
                                        helperText={fieldErrors.phase_template}
                                    />
                                </Grid>
                            </Grid>

                        </Grid>

                        <Divider sx={{ my: 2 }} />

                    </>
                )
            }

            {/* <Grid container spacing={3}>

                <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
                    <Typography variant="body1" color="text.primary">StakeHolders</Typography>
                    <Typography variant="body2" color="text.secondary">Identify primary contacts and responsible roles.</Typography>
                </Grid>

                <Grid container spacing={2} sx={{ flex: 1 }}>
                    {stakeholders.map((stakeholder: any) => (
                        <Grid size={{ xs: 12 }} key={stakeholder.id}>
                            <Card
                                variant="outlined"
                                sx={{
                                    borderRadius: 2,
                                    boxShadow: 0,
                                    "&:hover": { boxShadow: 1 },
                                }}
                            >
                                <CardContent
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Avatar alt={stakeholder.name} src={stakeholder.avatarUrl} sx={{ width: 48, height: 48 }} />
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={500}>
                                                {stakeholdersData.find((sh: any) => sh.id === stakeholder)?.derived_name || stakeholder.name || 'adkjbf'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {stakeholder.email}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleRemoveStakeholder(stakeholder.id)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                    <Button
                        variant="text"
                        startIcon={<AddIcon color="error" />}
                        onClick={handleOpenStakeholderDialog}
                        sx={{ color: "error.main", alignSelf: "start" }}
                    >
                        ASSIGN RESOURCE
                    </Button>
                </Grid>

            </Grid> */}

            <Grid container spacing={1.5} justifyContent={'end'}>
                <Button
                    variant="outlined"
                    color="primary"
                    disabled={isSubmitting}
                    onClick={() => { navigate(-1) }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? `${isEdit ? 'Editing' : 'Creating'} Project...` : isEdit ? 'Edit Project' : 'Create Project'}
                </Button>
            </Grid>

            <LocationSelection
                open={isDialogOpen}
                onClose={handleCloseDialog}
                onLocationSelect={handleLocationSelect}
                initialLocation={formData?.location ? formData?.location?.coordinates : null}
                title="Select Location"
                subTitle="Search for an address or click on the map to select your location"
                confirmButtonText="Use This Location"
                cancelButtonText="Cancel"
            />

        </Box>
    )
};